import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import { Reveal } from '../components/Reveal';
import { Search, ArrowUpRight, BookOpen } from 'lucide-react';

const Wissen = () => {
  const { t, lang } = useLang();
  const [q, setQ] = useState('');
  const articles = t('wissen.articles');
  const glossary = t('wissen.glossary');
  const query = q.trim().toLowerCase();

  const fArticles = useMemo(
    () => articles.filter((a) => !query || (a.title + ' ' + a.desc).toLowerCase().includes(query)),
    [articles, query]
  );
  const fGlossary = useMemo(
    () => glossary.filter((g) => !query || (g.term + ' ' + g.def).toLowerCase().includes(query)),
    [glossary, query]
  );
  const empty = fArticles.length === 0 && fGlossary.length === 0;

  return (
    <div>
      <SEO title={t('wissen.title')} description={t('wissen.lead')} path="/wissen" lang={lang} keywords={SEO_KEYWORDS[lang]} />
      <PageHeader label={t('wissen.label')} title={t('wissen.title')} lead={t('wissen.lead')} imageKey="concreteArch2" />
      <section className="bg-white py-14 lg:py-20">
        <div className="container-wide">
          {/* Search */}
          <div className="max-w-xl">
            <label htmlFor="wissen-search" className="sr-only">{t('wissen.searchLabel')}</label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
              <input
                id="wissen-search"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('common.searchPlaceholder')}
                className="w-full rounded-md border border-slate-300 bg-white pl-11 pr-4 py-3 text-[14px] text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25 transition"
              />
            </div>
          </div>

          {empty && <p className="mt-10 text-[15px] text-slate-500">{t('wissen.noResults')}</p>}

          {/* Articles */}
          {fArticles.length > 0 && (
            <div className="mt-10">
              <h2 className="text-[13px] font-bold uppercase tracking-[0.2em] text-slate-500">{t('wissen.articlesTitle')}</h2>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                {fArticles.map((a) => (
                  <Reveal key={a.key}>
                    <Link to={a.to} className="group h-full flex flex-col justify-between gap-4 p-6 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                      <div>
                        <h3 className="text-[16px] font-bold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors">{a.title}</h3>
                        <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{a.desc}</p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-blue-600 transition-colors" />
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {/* Glossary */}
          {fGlossary.length > 0 && (
            <div className="mt-14">
              <h2 className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.2em] text-slate-500">
                <BookOpen className="w-4 h-4" /> {t('wissen.glossaryTitle')}
              </h2>
              <dl className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {fGlossary.map((g) => (
                  <div key={g.term} className="border-l-2 border-blue-200 pl-4">
                    <dt className="text-[15px] font-bold text-slate-900">{g.term}</dt>
                    <dd className="mt-1 text-[13.5px] text-slate-600 leading-relaxed">{g.def}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default Wissen;
